import axios from "axios";

const ROLES = {
  ADMIN: "admin",
  PARTNER: "partner",
  CLIENT: "client",
};

// Clés localStorage liées à la session (effacées au logout / échec refresh)
const SESSION_KEYS = [
  "token",
  "refreshToken",
  "userRole",
  "userPhone",
  "profileId",
  "permissions",
  "memberships",
];

const clearSession = () => {
  SESSION_KEYS.forEach((k) => localStorage.removeItem(k));
};

const redirectToLogin = () => {
  clearSession();
  if (!window.location.pathname.includes("login")) {
    window.location.href = "/login";
  }
};

// Applique un nouveau couple de tokens (rotation du refresh token incluse)
const applyTokens = ({ accessToken, refreshToken }) => {
  localStorage.setItem("token", accessToken);
  if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
  axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
};

// --- Refresh token : dédupe les appels concurrents -------------------------
let refreshPromise = null;

const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise;

  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) {
    return Promise.reject(new Error("No refresh token"));
  }

  // Instance axios "nue" : pas d'Authorization expiré, pas d'intercepteurs (évite boucle)
  refreshPromise = axios
    .create({ baseURL: process.env.REACT_APP_API_BASE_URL })
    .post(
      "/auth/refresh-token",
      { refreshToken },
      {
        headers: {
          "X-Environment":
            localStorage.getItem("apiEnvironment") || "production",
        },
      }
    )
    .then((res) => {
      applyTokens(res.data);
      return res.data.accessToken;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const isAuthEndpoint = (url = "") =>
  url.includes("/auth/login") || url.includes("/auth/refresh-token");

const setupInterceptors = () => {
  // Request interceptor — loading indicator
  axios.interceptors.request.use(
    (config) => {
      if (!config.noLoading) {
        document.body.classList.add("loading-indicator");
      }
      return config;
    },
    (error) => {
      document.body.classList.remove("loading-indicator");
      return Promise.reject(error);
    }
  );

  // Response interceptor — loading indicator + refresh sur 401
  axios.interceptors.response.use(
    (response) => {
      if (!response.config.noLoading) {
        document.body.classList.remove("loading-indicator");
      }
      return response;
    },
    async (error) => {
      document.body.classList.remove("loading-indicator");

      const originalRequest = error.config;
      const status = error.response?.status;

      // 401 sur une requête normale, pas encore rejouée → tenter un refresh
      if (
        status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !isAuthEndpoint(originalRequest.url)
      ) {
        originalRequest._retry = true;
        try {
          const newToken = await refreshAccessToken();
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          // Refresh impossible (session invalide / refresh expiré) → logout
          redirectToLogin();
          return Promise.reject(refreshError);
        }
      }

      // 401 sur le refresh lui-même ou après un retry → logout
      if (status === 401) {
        redirectToLogin();
      }

      return Promise.reject(error);
    }
  );
};

const checkAuth = () => {
  const TOKEN = localStorage.getItem("token");
  const role = localStorage.getItem("userRole");

  const PUBLIC_ROUTES = [
    "login",
    "forgot-password",
    "register",
    "documentation",
  ];
  const currentPath = window.location.pathname;
  const isPublicPage = PUBLIC_ROUTES.some((route) =>
    currentPath.includes(route)
  );

  if (!TOKEN && !isPublicPage) {
    window.location.href = "/login";
    return;
  }

  if (TOKEN && role) {
    // Base URL — routes unifiées, pas de préfixe par rôle
    axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL;

    // Bearer token pour tous les rôles
    axios.defaults.headers.common["Authorization"] = `Bearer ${TOKEN}`;

    // En-tête environnement
    const env = localStorage.getItem("apiEnvironment") || "production";
    axios.defaults.headers.common["X-Environment"] = env;

    setupInterceptors();

    return TOKEN;
  }
};

export { ROLES, clearSession, applyTokens, refreshAccessToken };
export default checkAuth;
